import type { PageContentType } from '~/helpers/datalayer/types'
import { isRegisteredToken } from '~/utils/authCookies'

type BrazeEventProperties = NonNullable<Parameters<typeof import('@braze/web-sdk').logCustomEvent>[1]>

/**
 * Overrides for the context `logCustomEvent` otherwise reads live. Needed for an event awaited across
 * a route change, which would otherwise be stamped with the page the shopper ended up on.
 */
export type BrazeEventContext = {
  isLoggedIn?: boolean
  contentType?: PageContentType
}

export function useBrazeTracking() {
  function resolveLoggedInStatus(isLoggedIn?: boolean): boolean {
    if (isLoggedIn !== undefined) return isLoggedIn
    const token = useCookie<string | null>('access_token').value
    return isRegisteredToken(token) && useBrazeIdentity().isIdentityConfirmed()
  }

  function syncLoggedInStatus(isLoggedIn?: boolean): boolean {
    if (import.meta.server) return false

    const { $braze } = useNuxtApp()
    const user = $braze.sdk.getUser()
    if (!user) {
      $braze.debugLogger('Unable to sync is_logged_in: Braze has no current user.')
      return false
    }

    return user.setCustomUserAttribute('is_logged_in', resolveLoggedInStatus(isLoggedIn))
  }

  /**
   * Every Braze custom event goes through here, so `content_type` and `is_logged_in` are stamped once
   * rather than at each call site. Both are owned here: a value passed in `properties` is overwritten,
   * and the only way to influence them is the explicit `context` argument.
   */
  function logCustomEvent(name: string, properties?: BrazeEventProperties, context: BrazeEventContext = {}): boolean {
    if (import.meta.server) return false

    const { $braze } = useNuxtApp()
    return $braze.sdk.logCustomEvent(name, {
      ...properties,
      content_type: context.contentType ?? usePageContentType().contentType.value,
      is_logged_in: resolveLoggedInStatus(context.isLoggedIn),
    })
  }

  /**
   * logout clears persisted CID but deliberately does not changeUser, so at startup the SDK can still hold the previous shopper's profile.
   * To prevent marking the wrong profile as logged in, `useBrazeIdentity.ensureIdentity()` owns the 'true' sync.
   * This one is the 'false' path: whoever the SDK currently holds is genuinely not signed in.
   *
   * @returns `true` when the anonymous status was written; `false` when skipped or rejected by the SDK.
   */
  function syncLoggedInStatusIfAnonymous(): boolean {
    if (isRegisteredToken(useCookie<string | null>('access_token').value)) return false
    return syncLoggedInStatus(false)
  }

  return { syncLoggedInStatus, syncLoggedInStatusIfAnonymous, logCustomEvent }
}
